/**
 * Build an empty recipients template as CSV or XLSX without a server call.
 */

const HEADERS = ["name", "email", "phone_number"];

function triggerBlobDownload(blob, filename) {
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
}

export function downloadRecipientsCsvTemplate() {
    const blob = new Blob([`\uFEFF${HEADERS.join(",")}\n`], {
        type: "text/csv;charset=utf-8;",
    });
    triggerBlobDownload(blob, "campaign-recipients-template.csv");
}

function crc32(bytes) {
    let crc = 0xffffffff;
    for (let i = 0; i < bytes.length; i += 1) {
        crc ^= bytes[i];
        for (let j = 0; j < 8; j += 1) {
            crc = (crc >>> 1) ^ (crc & 1 ? 0xedb88320 : 0);
        }
    }
    return (crc ^ 0xffffffff) >>> 0;
}

function u16(value) {
    const buffer = new ArrayBuffer(2);
    new DataView(buffer).setUint16(0, value, true);
    return new Uint8Array(buffer);
}

function u32(value) {
    const buffer = new ArrayBuffer(4);
    new DataView(buffer).setUint32(0, value, true);
    return new Uint8Array(buffer);
}

function concatBytes(parts) {
    const total = parts.reduce((sum, part) => sum + part.length, 0);
    const out = new Uint8Array(total);
    let offset = 0;
    for (const part of parts) {
        out.set(part, offset);
        offset += part.length;
    }
    return out;
}

function encodeUtf8(text) {
    return new TextEncoder().encode(text);
}

function zipStore(files) {
    const localParts = [];
    const centralParts = [];
    let offset = 0;

    for (const file of files) {
        const nameBytes = encodeUtf8(file.name);
        const data = file.data;
        const crc = crc32(data);
        const localHeader = concatBytes([
            u32(0x04034b50),
            u16(20),
            u16(0),
            u16(0),
            u16(0),
            u16(0),
            u32(crc),
            u32(data.length),
            u32(data.length),
            u16(nameBytes.length),
            u16(0),
            nameBytes,
        ]);
        localParts.push(localHeader, data);

        const centralHeader = concatBytes([
            u32(0x02014b50),
            u16(20),
            u16(20),
            u16(0),
            u16(0),
            u16(0),
            u16(0),
            u32(crc),
            u32(data.length),
            u32(data.length),
            u16(nameBytes.length),
            u16(0),
            u16(0),
            u16(0),
            u16(0),
            u32(0),
            u32(offset),
            nameBytes,
        ]);
        centralParts.push(centralHeader);
        offset += localHeader.length + data.length;
    }

    const centralDirectory = concatBytes(centralParts);
    const endRecord = concatBytes([
        u32(0x06054b50),
        u16(0),
        u16(0),
        u16(files.length),
        u16(files.length),
        u32(centralDirectory.length),
        u32(offset),
        u16(0),
    ]);

    return concatBytes([...localParts, centralDirectory, endRecord]);
}

function buildXlsxBytes() {
    const contentTypes = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">
  <Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>
  <Default Extension="xml" ContentType="application/xml"/>
  <Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/>
  <Override PartName="/xl/worksheets/sheet1.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>
</Types>`;

    const rels = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
  <Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/>
</Relationships>`;

    const workbook = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">
  <sheets>
    <sheet name="recipients" sheetId="1" r:id="rId1"/>
  </sheets>
</workbook>`;

    const workbookRels = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
  <Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet1.xml"/>
</Relationships>`;

    const cells = HEADERS.map(
        (header, index) =>
            `<c r="${String.fromCharCode(65 + index)}1" t="inlineStr"><is><t>${header}</t></is></c>`
    ).join("");

    const sheet = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">
  <sheetData>
    <row r="1">${cells}</row>
  </sheetData>
</worksheet>`;

    return zipStore([
        { name: "[Content_Types].xml", data: encodeUtf8(contentTypes) },
        { name: "_rels/.rels", data: encodeUtf8(rels) },
        { name: "xl/workbook.xml", data: encodeUtf8(workbook) },
        { name: "xl/_rels/workbook.xml.rels", data: encodeUtf8(workbookRels) },
        { name: "xl/worksheets/sheet1.xml", data: encodeUtf8(sheet) },
    ]);
}

export function downloadRecipientsXlsxTemplate() {
    const bytes = buildXlsxBytes();
    const blob = new Blob([bytes], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    triggerBlobDownload(blob, "campaign-recipients-template.xlsx");
}

const SQL_TEMPLATE = `-- Phone list for call-center campaigns.
-- This file is parsed as data and is never executed as SQL.
INSERT INTO contacts (name, phone_number, email) VALUES
('Ali Rezaei', '09121234567', 'ali@example.com');
`;

export function downloadRecipientsSqlTemplate() {
    const blob = new Blob([SQL_TEMPLATE], {
        type: "text/plain;charset=utf-8;",
    });
    triggerBlobDownload(blob, "campaign-recipients-template.sql");
}

export function downloadRecipientsTemplate(format) {
    if (format === "xlsx" || format === "excel") {
        downloadRecipientsXlsxTemplate();
        return;
    }
    if (format === "sql") {
        downloadRecipientsSqlTemplate();
        return;
    }
    downloadRecipientsCsvTemplate();
}
